import { Component, ElementRef, inject } from '@angular/core';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { MAX_BIDDERS } from '../../modules/investigations/case-list/new-case-dialog/bidder-list-parser';
import { CaseCompany, CompanyCollectionState } from '../../modules/investigations/models/ciw.models';
import { CiwCaseContextService } from '../../modules/investigations/shared/ciw-case-context.service';
import { AppRoles, RolesService } from '../../shared/services/roles.service';
import { SharedModule } from '../../shared/shared.module';

const CIW_ROLE_PREFIX = 'Ciw';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, SharedModule, RouterLink, RouterLinkActive, MatButtonModule, MatListModule, MatIconModule, MatTooltip, TranslateModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  isSDDActive: boolean = false;
  isCDDActive: boolean = false;
  isBQActive: boolean = false;
  isSPActive: boolean = false;
  isInvestigationsActive: boolean = false;
  currentYear = new Date().getFullYear();
  AppRoles = AppRoles;

  sddMenu: any[] = [
    { id: 1, name: 'sideMenu.suppliers', svgIcon: 'people_alt', link: 'sdd/suppliers', isExternal: false, roles: [AppRoles.SDDAnalyst, AppRoles.SDDCompliance, AppRoles.SDDBasicUser] },
    { id: 2, name: 'sideMenu.subjectStarsCompanies', svgIcon: 'apartment', link: 'sdd/subject-to-stars-companies', isExternal: false, showTooltip: true, roles: [AppRoles.SDDAnalyst, AppRoles.SDDCompliance, AppRoles.SDDBasicUser] },
    { id: 3, name: 'sideMenu.myRequests', svgIcon: 'drive_file_move', link: 'sdd/my-requests', isExternal: false, roles: [AppRoles.SDDAnalyst, AppRoles.SDDCompliance, AppRoles.SDDBasicUser] },
    { id: 4, name: 'sideMenu.powerBiReports', svgIcon: 'insert_chart', isExternal: true, method: 'onPowerBiReports', roles: [AppRoles.SDDAnalyst, AppRoles.SDDCompliance, AppRoles.SDDBasicUser] },
    { id: 5, name: 'sideMenu.countries', svgIcon: 'public', link: 'sdd/countries', isExternal: false, roles: [AppRoles.SDDAnalyst, AppRoles.SDDCompliance, AppRoles.SDDBasicUser] }
  ];

  cddMenuCustomers: any[] = [
    { id: 1, name: 'sideMenu.dashboard', svgIcon: 'dashboard', link: 'cdd/dashboard', isExternal: false, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] },
    { id: 2, name: 'sideMenu.customers', svgIcon: 'people_alt', link: 'cdd/customers', isExternal: false, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance, AppRoles.CddMTCreator] },
    { id: 3, name: 'sideMenu.countries', svgIcon: 'public', link: 'cdd/countries', isExternal: false, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] }
  ];

  cddMenuOperations: any[] = [
    { id: 1, name: 'sideMenu.shareholderInformation', svgIcon: 'business_center_stroke', link: 'cdd/shareholder-information', isExternal: false, showTooltip: true, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] },
    { id: 2, name: 'sideMenu.managmentInformation', svgIcon: 'business_center', link: 'cdd/managment-information', isExternal: false, showTooltip: true, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] },
    { id: 2, name: 'sideMenu.banks', svgIcon: 'business', link: 'cdd/banks', isExternal: false, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] },
    { id: 2, name: 'sideMenu.vessels', svgIcon: 'monetization_on', link: 'cdd/vessels', isExternal: false, roles: [AppRoles.CddAnalyst, AppRoles.CddComplianceAnalyst, AppRoles.CddLegalCompliance] },
  ];

  bqMenu: any[] = [
    { id: 1, name: 'sideMenu.bqSuppliers', svgIcon: 'people_alt', link: 'bq/suppliers', isExternal: false, roles: [AppRoles.BqAdministrator, AppRoles.BqCompliance, AppRoles.BqMT, AppRoles.BqSCM] },
    { id: 2, name: 'sideMenu.bqCustomers', svgIcon: 'people_alt', link: 'bq/customers', isExternal: false, roles: [AppRoles.BqAdministrator, AppRoles.BqCompliance, AppRoles.BqMT, AppRoles.BqSCM] },
    { id: 3, name: 'sideMenu.bqMyQuestions', svgIcon: 'drive_file_move', link: 'bq/my-questions', isExternal: false, roles: [AppRoles.BqExternalUser] },
  ];

  spMenu: any[] = [
    { id: 1, name: 'sideMenu.spCustomers', svgIcon: 'people_alt', link: 'sp/customers', isExternal: false, roles: [AppRoles.SpAdmin, AppRoles.SpApprover, AppRoles.SpManagement, AppRoles.SpCddUser, AppRoles.SpSddUser] },
    { id: 2, name: 'sideMenu.spSuppliers', svgIcon: 'people_alt', link: 'sp/suppliers', isExternal: false, roles: [AppRoles.SpAdmin, AppRoles.SpApprover, AppRoles.SpManagement, AppRoles.SpCddUser, AppRoles.SpSddUser] },
    { id: 3, name: 'sideMenu.spScreenings', svgIcon: 'apartment', link: 'sp/screenings', isExternal: false, roles: [AppRoles.SpAdmin, AppRoles.SpApprover, AppRoles.SpManagement, AppRoles.SpCddUser, AppRoles.SpSddUser] },
    { id: 4, name: 'sideMenu.countries', svgIcon: 'public', link: 'sp/countries', isExternal: false, roles: [AppRoles.SpAdmin, AppRoles.SpApprover, AppRoles.SpManagement, AppRoles.SpCddUser, AppRoles.SpSddUser] },
    { id: 5, name: 'sideMenu.spSettings', svgIcon: 'business_center', link: 'sp/settings', isExternal: false, roles: [AppRoles.SpAdmin] }
  ];

  // Top-level CIW pages - grow this array as F13/F15 stories add more; F10's workspace tabs live
  // inside cases/:id, not here. The portal is the landing page. The match-review queue (AB#9980594) is
  // open to every CIW user in the nav; the API decides who may adjudicate and the page explains a 403.
  ciwMenu: any[] = [
    { id: 0, name: 'sideMenu.ciwPortal', svgIcon: 'dashboard', link: 'investigations/portal', isExternal: false },
    { id: 1, name: 'sideMenu.ciwCases', svgIcon: 'file_present', link: 'investigations/cases', isExternal: false },
    { id: 2, name: 'sideMenu.ciwMatchReview', svgIcon: 'people_alt', link: 'investigations/match-review', isExternal: false }
  ];

  // Rule set administration (AB#10153966/10153968). The API answers 403 to every role but CiwManager, so the
  // entry is not rendered for anyone else (showCiwManagerNav), and the route is guarded too.
  ciwManagerMenu: any[] = [
    { id: 1, name: 'sideMenu.ciwRuleSets', svgIcon: 'rule', link: 'investigations/rule-sets', isExternal: false }
  ];

  menus: any[] = [];

  // The open case's companies, shown under All Cases while a case is open.
  readonly caseContext = inject(CiwCaseContextService);
  // Shown in the explanation of a full case.
  readonly maxCompanies = MAX_BIDDERS;
  companyFilter = '';
  // Gated by featureFlags.investigations (config) AND a Ciw* role - both must pass to show the link.
  showInvestigationsNav = false;
  // CiwManager under any environment suffix (-Dev, -Test or none), on top of the same flag.
  showCiwManagerNav = false;

  constructor(
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    private router: Router,
    private rolesService: RolesService
  ) {
    matIconRegistry.addSvgIcon('people_alt', domSanitizer.bypassSecurityTrustResourceUrl('icons/people_alt.svg'));
    matIconRegistry.addSvgIcon('apartment', domSanitizer.bypassSecurityTrustResourceUrl('icons/apartment.svg'));
    matIconRegistry.addSvgIcon('drive_file_move', domSanitizer.bypassSecurityTrustResourceUrl('icons/drive_file_move.svg'));
    matIconRegistry.addSvgIcon('insert_chart', domSanitizer.bypassSecurityTrustResourceUrl('icons/insert_chart.svg'));
    matIconRegistry.addSvgIcon('public', domSanitizer.bypassSecurityTrustResourceUrl('icons/public.svg'));
    matIconRegistry.addSvgIcon('dashboard', domSanitizer.bypassSecurityTrustResourceUrl('icons/dashboard.svg'));
    matIconRegistry.addSvgIcon('business_center_stroke', domSanitizer.bypassSecurityTrustResourceUrl('icons/business_center_stroke.svg'));
    matIconRegistry.addSvgIcon('business_center', domSanitizer.bypassSecurityTrustResourceUrl('icons/business_center.svg'));
    matIconRegistry.addSvgIcon('business', domSanitizer.bypassSecurityTrustResourceUrl('icons/business.svg'));
    matIconRegistry.addSvgIcon('monetization_on', domSanitizer.bypassSecurityTrustResourceUrl('icons/monetization_on.svg'));
    matIconRegistry.addSvgIcon('lock', domSanitizer.bypassSecurityTrustResourceUrl('icons/lock.svg'));
    matIconRegistry.addSvgIcon('highlight_off', domSanitizer.bypassSecurityTrustResourceUrl('icons/highlight_off.svg'));
    matIconRegistry.addSvgIcon('save', domSanitizer.bypassSecurityTrustResourceUrl('icons/save.svg'));
    matIconRegistry.addSvgIcon('warning', domSanitizer.bypassSecurityTrustResourceUrl('icons/warning.svg'));
    matIconRegistry.addSvgIcon('file_present', domSanitizer.bypassSecurityTrustResourceUrl('icons/file_present.svg'));
    matIconRegistry.addSvgIcon('rule', domSanitizer.bypassSecurityTrustResourceUrl('icons/rule.svg'));
  }

  ngOnInit(): void {
    // Handle the current URL immediately (catches first load after OAuth redirect)
    this.updateMenuOptions(this.router.url);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateMenuOptions(event.urlAfterRedirects);
      }
    });

    this.updateInvestigationsNavVisibility();
  }

  private async updateInvestigationsNavVisibility(): Promise<void> {
    const flagOn = !!(environment as any).featureFlags?.investigations;
    this.showInvestigationsNav = flagOn && await this.rolesService.hasRolePrefix(CIW_ROLE_PREFIX);
    this.showCiwManagerNav = flagOn && await this.rolesService.hasRoleInAnyEnvironment(AppRoles.CiwManager);
  }

  // The search box only narrows what is listed; the selected company stays selected.
  visibleCompanies(companies: CaseCompany[]): CaseCompany[] {
    const needle = this.companyFilter.trim().toLowerCase();
    return needle ? companies.filter(c => c.name.toLowerCase().includes(needle) || (c.identifier ?? '').includes(needle)) : companies;
  }

  collectionIcon(state: CompanyCollectionState): string {
    return { collecting: 'sync', attention: 'warning_amber', done: 'check_circle' }[state];
  }

  // The dialog belongs to the case workspace: it owns the case's data and sits inside the route that provides the data
  // service, which this component, outside the route, cannot reach. It is asked through the case context.
  openAddCompany(): void {
    if (!this.caseContext.addCompanyBlockedReason) {
      this.caseContext.requestAddCompany();
    }
  }

  onMenuClick(menu: any) {
    if (menu.isExternal) {
      if (menu.method === 'onPowerBiReports') {
        this.onPowerBiReports();
      }
    }
  }

  isOverflowing(element: ElementRef | any, item: string): string | null {
    const el = element.nativeElement;
    return el?.scrollWidth > el?.clientWidth ? item : null;
  }

  private updateMenuOptions(url: string): void {
    this.isSDDActive = false;
    this.isCDDActive = false;
    this.isBQActive = false;
    this.isSPActive = false;
    this.isInvestigationsActive = false;
    if (url.includes('investigations')) {
      this.isInvestigationsActive = true;
    } else if (url.includes('cdd')) {
      this.isCDDActive = true;
    } else if (url.includes('bq')) {
      this.isBQActive = true;
    } else if (url.includes('sdd')) {
      this.isSDDActive = true;
    } else if (url.includes('sp')) {
      this.isSPActive = true;
    }
  }

  private onPowerBiReports() {
    window.open(environment.powerBIReports, "_blank");
  }
}
